/**
 * Omen icon textures (O-025) — canvas-drawn, colour-vision-safe silhouette symbols
 * for the three marker slots above a dungeon portal. One texture per symbolKey
 * (cached); bosses get a crown mark burned into the same texture so the boss read
 * never relies on colour alone (design doc §5.3 accessibility requirement).
 */

import * as THREE from 'three';

type Painter = (ctx: CanvasRenderingContext2D) => void;

const S = 96; // canvas size; icons drawn in a 96×96 box

function poly(ctx: CanvasRenderingContext2D, points: Array<[number, number]>): void {
  ctx.beginPath();
  ctx.moveTo(points[0]![0], points[0]![1]);
  for (const [x, y] of points.slice(1)) ctx.lineTo(x, y);
  ctx.closePath();
  ctx.fill();
}

/** Distinct SILHOUETTES per family — shape carries the meaning, not hue. */
const PAINTERS: Record<string, Painter> = {
  'gear-eye': (ctx) => {
    // Construct: gear ring + centre eye.
    ctx.beginPath();
    ctx.arc(48, 52, 24, 0, Math.PI * 2);
    ctx.fill();
    for (let index = 0; index < 8; index += 1) {
      const angle = (index / 8) * Math.PI * 2;
      ctx.save();
      ctx.translate(48 + Math.cos(angle) * 28, 52 + Math.sin(angle) * 28);
      ctx.rotate(angle);
      ctx.fillRect(-4, -6, 8, 12);
      ctx.restore();
    }
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(48, 52, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  },
  'many-claws': (ctx) => {
    // Swarm: three small claw triangles.
    poly(ctx, [
      [20, 70],
      [30, 38],
      [40, 70],
    ]);
    poly(ctx, [
      [40, 74],
      [50, 34],
      [60, 74],
    ]);
    poly(ctx, [
      [58, 68],
      [68, 40],
      [78, 68],
    ]);
  },
  'spread-wing': (ctx) => {
    // Flyer: spread wings.
    poly(ctx, [
      [10, 58],
      [44, 40],
      [48, 52],
      [52, 40],
      [86, 58],
      [52, 52],
      [48, 64],
      [44, 52],
    ]);
  },
  'split-bolt': (ctx) => {
    // Ranged: forked bolt.
    poly(ctx, [
      [42, 20],
      [58, 20],
      [50, 44],
      [66, 44],
      [38, 78],
      [46, 52],
      [32, 52],
    ]);
  },
  'tower-shield': (ctx) => {
    // Sentinel: tall shield.
    poly(ctx, [
      [32, 26],
      [64, 26],
      [64, 58],
      [48, 76],
      [32, 58],
    ]);
  },
  'fang-mark': (ctx) => {
    // Beast: twin fangs.
    poly(ctx, [
      [30, 30],
      [44, 30],
      [37, 68],
    ]);
    poly(ctx, [
      [52, 30],
      [66, 30],
      [59, 68],
    ]);
  },
  'thorn-leaf': (ctx) => {
    // Living growth: leaf + thorn.
    ctx.beginPath();
    ctx.ellipse(48, 50, 16, 26, 0.5, 0, Math.PI * 2);
    ctx.fill();
    poly(ctx, [
      [58, 68],
      [74, 76],
      [62, 58],
    ]);
  },
  'spore-cloud': (ctx) => {
    // Spore: clustered circles.
    for (const [x, y, radius] of [
      [38, 44, 14],
      [58, 40, 10],
      [50, 60, 12],
      [66, 58, 8],
      [32, 62, 8],
    ] as Array<[number, number, number]>) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  },
  'veiled-eye': (ctx) => {
    // Spirit: lidded eye.
    ctx.beginPath();
    ctx.ellipse(48, 52, 28, 15, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(48, 52, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  },
  unknown: (ctx) => {
    // Fallback rune.
    ctx.font = 'bold 52px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('?', 48, 54);
  },
};

function drawCrown(ctx: CanvasRenderingContext2D): void {
  poly(ctx, [
    [30, 22],
    [38, 10],
    [48, 20],
    [58, 10],
    [66, 22],
  ]);
}

const cache = new Map<string, THREE.Texture>();

/** Shrouded (null symbol) → dim empty slot; boss → crown mark added. */
export function omenIconTexture(symbolKey: string | null, boss: boolean): THREE.Texture {
  const key = `${symbolKey ?? '∅'}::${boss ? 'boss' : 'plain'}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  // Slot backplate: rounded dark plate so icons read against any sky.
  ctx.fillStyle = 'rgba(18,24,30,0.78)';
  ctx.beginPath();
  ctx.roundRect(4, 4, S - 8, S - 8, 14);
  ctx.fill();
  ctx.strokeStyle = 'rgba(200,162,58,0.9)'; // established gold trim
  ctx.lineWidth = 3;
  ctx.stroke();
  if (symbolKey) {
    ctx.fillStyle = boss ? '#e8d9a0' : '#cfd9dd';
    (PAINTERS[symbolKey] ?? PAINTERS['unknown'])!(ctx);
    if (boss) {
      ctx.fillStyle = '#e8d9a0';
      drawCrown(ctx);
    }
  } else {
    ctx.fillStyle = 'rgba(120,130,138,0.5)';
    PAINTERS['unknown']!(ctx);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  cache.set(key, texture);
  return texture;
}
